import * as React from 'react';

import { enumUserSortOption } from '@rematter/frontend-lib-api-client';
import {
  Button,
  Loading,
  Popover,
  PopoverDialog,
  PopoverTrigger,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { Mail, Send, X } from 'lucide-react';

import { contactsQueryAtom } from '../../_graphql/queryContacts';
import { usersQueryAtom } from '../../_graphql/queryUsers';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { useIdentity } from '~/shared/hooks';
import { useDebouncedValue } from '~/shared/hooks/useDebounce';

type Props = {
  onSend: (emails: string[]) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const $contacts = contactsQueryAtom(() => ({
  variables: {} as never,
  lazy: true,
}));

const $users = usersQueryAtom(() => ({
  variables: {} as never,
  lazy: true,
}));

export const EmailButton: React.FC<Props> = ({
  onSend,
  isDisabled = false,
  isLoading = false,
}) => {
  const t = useTranslations();
  const [selectedEmails, setSelectedEmails] = React.useState<string[]>([]);
  const [emailInput, setEmailInput] = React.useState('');

  const { user } = useIdentity();

  const debouncedSearchTerm = useDebouncedValue(emailInput, 300);

  const shouldQueryContacts = debouncedSearchTerm.length > 0;
  const shouldQueryUsers = debouncedSearchTerm.length > 0 && user?.id != null;

  const {
    data: contactsData,
    isLoading: isLoadingContacts,
    refetch: fetchContacts,
  } = useAtomValue($contacts);
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    refetch: fetchUsers,
  } = useAtomValue($users);

  React.useEffect(() => {
    if (shouldQueryContacts) {
      fetchContacts({
        limit: 50,
        searchTerm: debouncedSearchTerm,
      });
    }
    if (shouldQueryUsers) {
      fetchUsers({
        userId: user?.id as string,
        sorting: [enumUserSortOption.FirstNameAsc],
        searchTerm: debouncedSearchTerm,
        limit: 50,
      });
    }
  }, [
    shouldQueryContacts,
    shouldQueryUsers,
    fetchContacts,
    fetchUsers,
    debouncedSearchTerm,
    user?.id,
  ]);

  const isLoadingEmails = isLoadingContacts || isLoadingUsers;

  const availableEmails = React.useMemo(
    () =>
      [
        ...(contactsData?.entries?.map((contact) => contact.email) ?? []),
        ...(usersData?.map((user) => user.email) ?? []),
      ].filter((email) => email !== null),
    [contactsData?.entries, usersData],
  );

  // Filtered emails are emails that are not already selected and are suggested based on matching input
  const filteredEmails = React.useMemo(
    () =>
      emailInput.length > 0
        ? availableEmails
            .filter((email) => !selectedEmails.includes(email))
            .filter((email) => email.includes(emailInput))
        : [],
    [availableEmails, selectedEmails, emailInput],
  );

  const handleSend = async () => {
    const allEmails = [...selectedEmails];
    if (emailInput && !allEmails.includes(emailInput)) {
      allEmails.push(emailInput);
    }

    if (allEmails.length > 0) {
      await onSend(allEmails);
      setSelectedEmails([]);
      setEmailInput('');
    }
  };

  const toggleEmail = (email: string) => {
    setSelectedEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
    );
    setEmailInput('');
  };

  const removeEmail = (email: string) => {
    setSelectedEmails((prev) => prev.filter((e) => e !== email));
  };

  return (
    <PopoverTrigger>
      <Button
        variant="outline"
        size="default"
        icon={isLoading ? undefined : <Send />}
        isDisabled={isDisabled}
        className="w-[150px]"
      >
        {isLoading
          ? t.bookingDetail.documents.actions.sending
          : t.bookingDetail.documents.actions.sendViaEmail}
      </Button>
      <Popover offset={10}>
        <PopoverDialog className="w-[28rem] p-4">
          <div className="flex items-center justify-between">
            <h3 className="mb-4 text-sm font-bold text-neutral">
              {t.bookingDetail.documents.email.title}
            </h3>
          </div>

          <div className="flex flex-col gap-2">
            {/* Selected emails display */}
            <div className="flex flex-wrap items-center gap-1">
              {selectedEmails.map((email) => (
                <div
                  key={email}
                  className="flex min-h-4 items-center gap-1 rounded-md bg-primary/10 p-2 py-1 text-sm text-primary/80"
                >
                  {email}
                  <button
                    onClick={() => removeEmail(email)}
                    className="ml-1 hover:text-primary/60"
                  >
                    <X size={8} />
                  </button>
                </div>
              ))}
              {/* Custom email input */}
              <div className="flex flex-row items-center justify-start gap-1 rounded-md px-1 py-2">
                <Mail size={14} />
                <input
                  type="email"
                  placeholder={t.bookingDetail.documents.email.placeholder}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="ml-1 text-sm focus:ring-0 focus:outline-none"
                />
              </div>
            </div>
            {/* Contact suggestions */}
            {filteredEmails.length > 0 && !isLoadingEmails && (
              <div>
                <div className="mb-1 text-sm text-neutral">
                  {t.bookingDetail.documents.email.found}
                </div>
                <div className="flex flex-col gap-2">
                  {filteredEmails.map((email) => (
                    <div
                      key={email}
                      className="cursor-pointer text-sm text-neutral"
                      onClick={() => toggleEmail(email)}
                    >
                      {email}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isLoadingEmails && <Loading isLoading={isLoadingEmails} />}
            {/* Send button */}
            <div className="flex flex-row justify-end">
              <Button
                variant="solid"
                size="default"
                className="self-end"
                onPress={handleSend}
                isDisabled={selectedEmails.length === 0 && !emailInput}
              >
                {t.bookingDetail.documents.email.send}
              </Button>
            </div>
          </div>
        </PopoverDialog>
      </Popover>
    </PopoverTrigger>
  );
};
